import { useMemo } from 'react';
import { List, ListItemButton, ListItemText, Typography, ListSubheader } from '@mui/material';
import { format } from 'date-fns';
import { useParams, useLocation } from 'react-router-dom';
import { getEndpointWithParams } from '@/routes/config';
import { TCheckinHistory } from '@/types/api';
import { Paths } from '@/types/routes';
import { defaultCheckinDateFormat } from '@/utils/checkins';
import { getComparator } from '@/utils/order';
import styles from './CheckinsList.styles';

type CheckinsListType = {
  checkinsHistory: TCheckinHistory[];
  userName?: string;
};

const CheckinsList = function ({ checkinsHistory, userName }: CheckinsListType): JSX.Element {
  const { checkinId: currentCheckinId, userId } = useParams();
  const { pathname } = useLocation();

  const viewAllLinkTo = useMemo(() => {
    if (userId) {
      return getEndpointWithParams(Paths.USER_CHECKINS, { userId });
    }
    if (currentCheckinId && !userId) {
      return Paths.MY_CHECKINS;
    }
    return Paths.MY_CURRENT_CHECKIN;
  }, [currentCheckinId, userId]);

  const listHeadlineText = useMemo(() => {
    if (userName && !pathname.includes(Paths.MY_CHECKINS)) {
      return `${userName}'s Recent Check-ins`;
    }

    return 'My Recent Check-ins';
  }, [userName, pathname]);

  const renderCheckinsList = useMemo(() => {
    const lastFiveCheckinsSorted = checkinsHistory
      .sort(getComparator('desc', 'dueDate'))
      .slice(0, 5);

    return lastFiveCheckinsSorted.map(({ checkinId, submittedDate, endDate }) => {
      const myCheckinUrl = getEndpointWithParams(Paths.MY_CHECKIN_DETAILS, { checkinId });
      const userCheckinUrl = getEndpointWithParams(Paths.USER_CHECKIN_DETAILS, {
        checkinId,
        userId,
      });

      const endDateFormatted = format(new Date(endDate), defaultCheckinDateFormat);
      const submittedDateFormatted =
        submittedDate && format(new Date(submittedDate), defaultCheckinDateFormat);

      const submittedMessage = `Submitted on ${submittedDateFormatted}`;
      const pendingMessage = 'Pending';

      return (
        <ListItemButton
          sx={currentCheckinId === checkinId ? styles.selectedItem : {}}
          key={checkinId}
          component="a"
          disabled={!!userId && !submittedDate}
          href={userId ? userCheckinUrl : myCheckinUrl}
        >
          <ListItemText
            primary={
              <>
                <Typography component="span" display="block" variant="body2" color="text.primary">
                  Due date: {endDateFormatted}
                </Typography>

                <Typography component="span" variant="body2" color="text.secondary">
                  {submittedDate ? submittedMessage : pendingMessage}
                </Typography>
              </>
            }
          />
        </ListItemButton>
      );
    });
  }, [checkinsHistory, currentCheckinId, userId]);

  return (
    <List
      data-testid="list-of-checkins"
      subheader={
        <ListSubheader sx={styles.alignedCentered} component="h3">
          {listHeadlineText}
        </ListSubheader>
      }
      component="nav"
    >
      {renderCheckinsList}
      <ListItemButton data-testid="view-all-checkins-link" component="a" href={viewAllLinkTo}>
        <ListItemText sx={styles.viewAllLink} primary="View all" />
      </ListItemButton>
    </List>
  );
};

export default CheckinsList;
